/**
 * CDID page — Queue (is it keeping up), Activity (run history), Tickets (live
 * selection queue), Leases and Settings, using the same tab treatment as the
 * Product Emails page.
 *
 * Queue flow used to render above the tab bar on every tab. It is a tab of its
 * own now (TPF, 2026-09-16): a 14-day arrivals-vs-drain chart is not context
 * for reading a single run's stage chips, and printing it over all five tabs
 * cost the same vertical space five times.
 */
import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/utils';
import { useHashTab } from './shared';
import { CdidCyclesTab } from './CdidCyclesTab';
import { CdidTicketsTab } from './CdidTicketsTab';
import { CdidSettingsTab } from './CdidSettingsTab';
import { LeaseStatusSection } from './LeaseStatusSection';
import { CdidFlowSection } from './CdidFlowSection';

type TabId = 'queue' | 'activity' | 'tickets' | 'leases' | 'settings';

const TABS: { id: TabId; label: string }[] = [
  // FIRST, and therefore the default a bare URL lands on (TAB_IDS order decides
  // that). Queue flow answers "is CDID keeping up", which is the question you
  // arrive with; the run history answers "what happened in run N", which is the
  // question you arrive with second.
  { id: 'queue', label: 'Queue' },
  { id: 'activity', label: 'Activity' },
  { id: 'tickets', label: 'Tickets' },
  // Its own tab rather than a strip on Activity: the lease view answers a
  // different question ("who holds the accounts right now") from the run
  // history, it polls on its own cadence, and a hash makes it linkable from an
  // incident.
  { id: 'leases', label: 'Leases' },
  { id: 'settings', label: 'Settings' },
];
/** Derived from TABS so the hash vocabulary can never drift from the rendered tabs. */
const TAB_IDS: TabId[] = TABS.map((t) => t.id);

export default function CdidPage() {
  // URL hash owns the selection — see useHashTab. TAB_IDS order defines the
  // default, so `activity` is what a bare URL shows.
  const [activeTab, setActiveTab] = useHashTab(TAB_IDS);

  return (
    <div className="space-y-4">
      {/* The tab-by-tab explainer and the live next-run tail are gone (TPF,
          2026-09-09): the tabs name themselves, and the strap is now just what
          the acronym stands for. */}
      <PageHeader title="CDID" subtitle="Continuous Development-Integration-Deploy" />

      <div className="border-b border-zinc-200 dark:border-zinc-700">
        <nav className="-mb-px flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'border-b-2 pb-3 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'queue' && <CdidFlowSection />}
      {activeTab === 'activity' && <CdidCyclesTab />}
      {activeTab === 'tickets' && <CdidTicketsTab />}
      {activeTab === 'leases' && <LeaseStatusSection />}
      {activeTab === 'settings' && <CdidSettingsTab />}
    </div>
  );
}
